/**
 * Header popover fallbacks when the native Popover API is missing
 * (e.g. Safari on iOS 16): mobile menu, mobile search, mini cart.
 *
 * Loaded from `layout/theme.liquid` (and `password.liquid`) as a classic
 * deferred script, not `type="module"`, so this file cannot use `import` /
 * `export`. The IIFE keeps helpers and constants off `window` while still
 * applying `'use strict'` to the implementation body.
 */

(function () {
  'use strict';

  if (typeof HTMLElement === 'undefined') {
    return;
  }

  if (typeof HTMLElement.prototype.showPopover === 'function') {
    return;
  }

  const NAV_ID = 'site-mobile-navigation';
  const MENU_OPEN_CLASS = 'mobile-nav-component--fallback-open';

  /**
   * Id of the drawer the last-clicked `.menu-drawer-button` targets. Defaults
   * to the control drawer; A/B variant hamburgers point at their own drawer.
   * @type {string}
   */
  let activeNavId = NAV_ID;

  const SEARCH_ID = 'mobile-search-popover';
  const SEARCH_OPEN_CLASS = 'mobile-search-popover--fallback-open';

  const CART_ID = 'mini-cart-wrapper';
  const CART_OPEN_CLASS = 'mini-cart-wrapper--fallback-open';

  /**
   * Returns the mobile navigation drawer root element.
   * @returns {HTMLElement | null}
   */
  function getNav() {
    return document.getElementById(activeNavId);
  }

  /**
   * Returns the menu toggle button that targets the active mobile nav popover.
   * @returns {Element | null}
   */
  function getMenuButton() {
    return document.querySelector(
      '.menu-drawer-button[popovertarget="' + activeNavId + '"]',
    );
  }

  /**
   * Returns every menu toggle button whose target drawer exists in the DOM.
   * @returns {Element[]}
   */
  function getMenuButtons() {
    return [...document.querySelectorAll('.menu-drawer-button[popovertarget]')].filter(
      (btn) => !!document.getElementById(btn.getAttribute('popovertarget')),
    );
  }

  /**
   * Returns the mobile search popover element.
   * @returns {HTMLElement | null}
   */
  function getSearchPopover() {
    return document.getElementById(SEARCH_ID);
  }

  /**
   * Returns the mobile search trigger button.
   * @returns {Element | null}
   */
  function getSearchButton() {
    return document.querySelector(
      '.mobile-search-button[popovertarget="' + SEARCH_ID + '"]',
    );
  }

  /**
   * Returns the mini cart wrapper element.
   * @returns {HTMLElement | null}
   */
  function getCartWrapper() {
    return document.getElementById(CART_ID);
  }

  /**
   * Returns the mini cart trigger button.
   * @returns {Element | null}
   */
  function getCartButton() {
    const buttons = document.querySelectorAll('[data-cart-btn]');

    return [...buttons].find((btn) => btn.offsetParent !== null) || buttons[0] || null;
  }

  /**
   * Whether the mobile menu is open in fallback mode.
   * @returns {boolean}
   */
  function isMenuOpen() {
    const nav = getNav();

    return !!(nav && nav.classList.contains(MENU_OPEN_CLASS));
  }

  /**
   * Whether the mobile search popover is open in fallback mode.
   * @returns {boolean}
   */
  function isSearchOpen() {
    const el = getSearchPopover();

    return !!(el && el.classList.contains(SEARCH_OPEN_CLASS));
  }

  /**
   * Whether the mini cart is open in fallback mode.
   * @returns {boolean}
   */
  function isCartOpen() {
    const el = getCartWrapper();

    return !!(el && el.classList.contains(CART_OPEN_CLASS));
  }

  /**
   * Removes body scroll lock when no fallback popover is open.
   * @returns {void}
   */
  function clearNoScrollIfIdle() {
    if (!isMenuOpen() && !isSearchOpen() && !isCartOpen()) {
      document.documentElement.classList.remove('noScroll');
      document.body.classList.remove('noScroll');
    }
  }

  /**
   * Opens or closes the mobile menu using fallback classes and ARIA.
   * @param {boolean} open
   * @returns {void}
   */
  function setMenuOpen(open) {
    const nav = getNav();
    const btn = getMenuButton();

    if (!nav) {
      return;
    }

    if (open) {
      nav.classList.add(MENU_OPEN_CLASS);
      document.documentElement.classList.add('noScroll');
      document.body.classList.add('noScroll');
      if (btn) {
        btn.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    nav.classList.remove(MENU_OPEN_CLASS);
    clearNoScrollIfIdle();

    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Opens or closes the mobile search popover using fallback classes and ARIA.
   * @param {boolean} open
   * @returns {void}
   */
  function setSearchOpen(open) {
    const pop = getSearchPopover();
    const btn = getSearchButton();

    if (!pop) {
      return;
    }

    if (open) {
      pop.classList.add(SEARCH_OPEN_CLASS);
      document.documentElement.classList.add('noScroll');
      document.body.classList.add('noScroll');
      if (btn) {
        btn.setAttribute('aria-expanded', 'true');
      }
      requestAnimationFrame(function () {
        const input = pop.querySelector('input[type="search"]');

        if (input) {
          input.focus();
        }
      });
      return;
    }

    pop.classList.remove(SEARCH_OPEN_CLASS);
    clearNoScrollIfIdle();

    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Opens or closes the mini cart using fallback classes and ARIA.
   * Dispatches a `toggle` event on the wrapper when state changes.
   * @param {boolean} open
   * @returns {void}
   */
  function setCartOpen(open) {
    const wrapper = getCartWrapper();
    const btn = getCartButton();

    if (!wrapper) {
      return;
    }

    if (open) {
      wrapper.classList.add(CART_OPEN_CLASS);
      document.documentElement.classList.add('noScroll');
      document.body.classList.add('noScroll');
      if (btn) {
        btn.setAttribute('aria-expanded', 'true');
      }
      wrapper.dispatchEvent(new Event('toggle'));
      return;
    }

    wrapper.classList.remove(CART_OPEN_CLASS);
    clearNoScrollIfIdle();

    if (btn) {
      btn.setAttribute('aria-expanded', 'false');
    }

    wrapper.dispatchEvent(new Event('toggle'));
  }

  /**
   * Closes the search popover when the backdrop (the popover element itself)
   * is clicked, then moves focus to the search trigger.
   * @param {MouseEvent} event
   * @returns {void}
   */
  function onSearchPopoverClick(event) {
    const pop = getSearchPopover();

    if (!pop || !pop.classList.contains(SEARCH_OPEN_CLASS)) {
      return;
    }

    if (event.target === pop) {
      event.preventDefault();
      setSearchOpen(false);

      const btn = getSearchButton();

      if (btn) {
        btn.focus();
      }
    }
  }

  /**
   * Capturing-phase click handler: opens/closes popovers from triggers and
   * handles `popovertargetaction="hide"` controls.
   * @param {MouseEvent} event
   * @returns {void}
   */
  function onDocumentClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const menuHide = target.closest(
      '[popovertarget="' + activeNavId + '"][popovertargetaction="hide"]',
    );

    if (menuHide) {
      event.preventDefault();
      setMenuOpen(false);
      return;
    }

    const cartHide = target.closest(
      '[popovertarget="' + CART_ID + '"][popovertargetaction="hide"]',
    );

    if (cartHide) {
      event.preventDefault();
      setCartOpen(false);

      const cbtn = getCartButton();

      if (cbtn) {
        cbtn.focus();
      }
      return;
    }

    const menuBtn = target.closest('.menu-drawer-button');

    const menuTargetId = menuBtn ? menuBtn.getAttribute('popovertarget') : null;

    if (menuTargetId && document.getElementById(menuTargetId)) {
      event.preventDefault();
      if (menuTargetId !== activeNavId) {
        setMenuOpen(false);
        activeNavId = menuTargetId;
      }
      setMenuOpen(true);
      return;
    }

    const searchBtn = target.closest('.mobile-search-button');

    if (searchBtn && searchBtn.getAttribute('popovertarget') === SEARCH_ID) {
      event.preventDefault();
      setSearchOpen(true);
      return;
    }

    const cartBtn = target.closest('[data-cart-btn]');

    if (cartBtn && cartBtn.getAttribute('popovertarget') === CART_ID) {
      event.preventDefault();
      setCartOpen(!isCartOpen());
    }
  }

  /**
   * Dismisses the topmost open fallback layer on Escape and restores focus
   * to its trigger (search, cart, then menu).
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  function onKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    if (isSearchOpen()) {
      event.preventDefault();
      setSearchOpen(false);

      const sb = getSearchButton();

      if (sb) {
        sb.focus();
      }
      return;
    }

    if (isCartOpen()) {
      event.preventDefault();
      setCartOpen(false);

      const cb = getCartButton();

      if (cb) {
        cb.focus();
      }
      return;
    }

    if (isMenuOpen()) {
      event.preventDefault();
      setMenuOpen(false);

      const mb = getMenuButton();

      if (mb) {
        mb.focus();
      }
    }
  }

  /**
   * Registers ARIA attributes and document-level listeners when at least one
   * header popover pair exists in the DOM.
   * @returns {void}
   */
  function init() {
    const menuBtns = getMenuButtons();
    const searchPop = getSearchPopover();
    const searchBtn = getSearchButton();
    const cartWrap = getCartWrapper();
    const cartBtn = getCartButton();

    const needsListeners =
      menuBtns.length > 0 || (searchPop && searchBtn) || (cartWrap && cartBtn);

    if (!needsListeners) {
      return;
    }

    menuBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', btn.getAttribute('popovertarget'));
    });

    if (searchPop && searchBtn) {
      searchBtn.setAttribute('aria-expanded', 'false');
      searchBtn.setAttribute('aria-controls', SEARCH_ID);
      searchPop.addEventListener('click', onSearchPopoverClick);
    }

    if (cartWrap && cartBtn) {
      cartBtn.setAttribute('aria-expanded', 'false');
      cartBtn.setAttribute('aria-controls', CART_ID);
    }

    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeyDown, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
